import type { FormSchemaResolved } from '#layers/block-engine/public';
import * as z from 'zod/v4-mini';
import { AuthProtocol, defineAuthProvider } from '#layers/access/types/auth-providers/define';
import { oauth2, oauth2ConfigCreateSchema, oauth2ConfigSchema } from '#layers/access/utils/auth-providers/protocols/oauth2';
import { applyOAuth2PresetOptions, oauth2PresetDefaults } from './oauth2-preset';

const GITHUB_WIRE_DEFAULTS = {
	authorizationEndpoint: 'https://github.com/login/oauth/authorize',
	tokenEndpoint: 'https://github.com/login/oauth/access_token',
	userinfoEndpoint: 'https://api.github.com/user',
	scopes: ['user:email'],
	claimMappings: { sub: 'id', email: 'email' },
} as const;

const githubConfigSchema = z.object({
	...oauth2ConfigSchema.def.shape,
	scopes: z.array(z.string()),
});

const githubConfigCreateSchema = z.object({
	...oauth2ConfigCreateSchema.def.shape,
	scopes: z.array(z.string()),
});

const GITHUB_DEFAULTS = oauth2PresetDefaults(GITHUB_WIRE_DEFAULTS);

const GITHUB_DESCRIPTIONS: Record<string, string> = {
	clientId: '$t:sso_providers.github.client_id_hint',
	clientSecret: '$t:sso_providers.github.client_secret_hint',
};

function applyGitHubDefaults(items: FormSchemaResolved<any>): FormSchemaResolved<any> {
	return applyOAuth2PresetOptions(items, {
		defaults: GITHUB_DEFAULTS,
		descriptions: GITHUB_DESCRIPTIONS,
	});
}

export const github = defineAuthProvider({
	id: 'github',
	name: '$t:sso_providers.github.name',
	description: '$t:sso_providers.github.description',
	protocol: AuthProtocol.OAuth2,
	icon: {
		default: 'logos:github-icon',
		dark: 'simple-icons:github',
	},
	docsUrl: 'https://docs.github.com/apps/oauth-apps/building-oauth-apps/creating-an-oauth-app',
	walkthroughStepKeys: [
		{
			key: 'sso_providers.github.walkthrough.create_app',
			href: 'https://github.com/settings/applications/new',
		},
		'sso_providers.github.walkthrough.redirect',
		'sso_providers.github.walkthrough.credentials',
	],
	extends: oauth2,
	schema: githubConfigCreateSchema,
	editSchema: githubConfigSchema,
	options: ({ form, context }) =>
		form(applyGitHubDefaults(context.base?.resolvedOptions ?? []) as never),
});
